import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Star, Camera, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API, CIGAR_PLACEHOLDER } from '../config/constants';
import BottomNav from '../components/BottomNav';
import CameraModal from '../components/modals/CameraModal';

const AddCigarPage = () => {
  const [search, setSearch] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [searching, setSearching] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [formData, setFormData] = useState({
    brand: '',
    name: '',
    strength: '',
    origin: '',
    size: '',
    wrapper: '',
    price_range: ''
  });
  const { token } = useAuth();
  const navigate = useNavigate();

  const handleAISearch = async () => {
    if (!search.trim()) {
      toast.error('Please enter a cigar name');
      return;
    }

    if (!token) {
      toast.error('Please sign in to add cigars');
      navigate('/auth');
      return;
    }

    setSearching(true);
    setAiResult(null);
    try {
      // Use AI to search/identify the cigar from the internet
      const res = await axios.post(
        `${API}/cigars/search-ai`,
        { description: search.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.parsed_data) {
        // AI found cigar information
        const parsedData = res.data.parsed_data;
        const alreadyExists = res.data.already_exists;
        const suggestions = res.data.suggestions || [];
        
        if (alreadyExists) {
          // This exact cigar already exists in the database
          setAiResult({ 
            parsed_data: parsedData,
            alreadyExists: alreadyExists
          });
          toast.info(`"${parsedData.brand} ${parsedData.name}" is already in the database!`);
        } else if (suggestions.length > 0) {
          // Similar cigars found - show suggestions (no toast, UI shows them)
          setAiResult({ 
            parsed_data: parsedData,
            suggestions: suggestions,
            canAdd: true 
          });
        } else {
          // AI identified a real cigar not in database - offer to add it
          setAiResult({ 
            parsed_data: parsedData,
            canAdd: true 
          });
        }
      } else if (res.data.not_found) {
        // AI couldn't identify this as a real cigar
        toast.error(res.data.reason || 'This does not appear to be a recognized cigar.');
        setAiResult({
          notFound: true,
          reason: res.data.reason,
          searchTerm: search
        });
      } else {
        toast.error('Could not identify this cigar. Try Custom Search to add it manually.');
      }
    } catch (error) {
      console.error('AI Search error:', error);
      toast.error('Failed to search for cigar');
    } finally {
      setSearching(false);
    }
  };

  const handleConfirmAdd = async () => {
    if (!aiResult) return;

    setConfirming(true);
    try {
      const res = await axios.post(
        `${API}/cigars/confirm-add`,
        aiResult,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Cigar added successfully!');
      navigate(`/cigar/${res.data.id}`);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(error.response.data.detail || 'This cigar already exists in the system');
      } else {
        toast.error('Failed to add cigar');
      }
    } finally {
      setConfirming(false);
    }
  };

  const handleCancelAI = () => {
    setAiResult(null);
    setSearch('');
  };

  const handleCustomSearch = async () => {
    // Check if at least one search criteria is provided
    if (!formData.brand && !formData.name && !formData.strength && !formData.origin && !formData.wrapper && !formData.size && !formData.price_range) {
      toast.error('Please enter at least one search criteria');
      return;
    }

    if (!token) {
      toast.error('Please sign in to search cigars');
      navigate('/auth');
      return;
    }

    setSearching(true);
    try {
      // Build search params - include all filters
      const params = { limit: 50 }; // Limit to 50 results
      
      // Text search for brand/name
      let searchQuery = '';
      if (formData.brand) searchQuery = formData.brand;
      if (formData.name) searchQuery += ' ' + formData.name;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      
      // Add all filter parameters
      if (formData.strength) params.strength = formData.strength;
      if (formData.origin) params.origin = formData.origin;
      if (formData.wrapper) params.wrapper = formData.wrapper;
      if (formData.size) params.size = formData.size;
      if (formData.price_range) params.price = formData.price_range;
      
      const res = await axios.get(`${API}/cigars`, { params });

      // Filter results further on client side for exact matches on all specified filters
      let filteredResults = res.data;
      
      if (formData.origin) {
        filteredResults = filteredResults.filter(c => 
          c.origin && c.origin.toLowerCase().includes(formData.origin.toLowerCase())
        );
      }
      if (formData.strength) {
        filteredResults = filteredResults.filter(c => 
          c.strength && c.strength.toLowerCase().includes(formData.strength.toLowerCase())
        );
      }
      if (formData.wrapper) {
        filteredResults = filteredResults.filter(c => 
          c.wrapper && c.wrapper.toLowerCase().includes(formData.wrapper.toLowerCase())
        );
      }
      if (formData.size) {
        filteredResults = filteredResults.filter(c => 
          c.size && c.size.toLowerCase().includes(formData.size.toLowerCase())
        );
      }

      if (filteredResults.length > 0) {
        // Show results
        setShowCustom(false);
        setAiResult({ searchResults: filteredResults });
        toast.success(`Found ${filteredResults.length} matching cigars`);
      } else {
        // No results, ask if they want to add new cigar
        setShowCustom(false);
        toast.info('No matching cigars found. Would you like to add it as new?');
        setAiResult({ noResults: true, formData });
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleAddNewCigar = async () => {
    if (!token) {
      toast.error('Please sign in to add cigars');
      navigate('/auth');
      return;
    }

    if (!formData.brand || !formData.name) {
      toast.error('Brand and name are required');
      return;
    }

    setConfirming(true);
    try {
      const res = await axios.post(`${API}/cigars`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cigar added!');
      navigate(`/cigar/${res.data.id}`);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(error.response.data.detail || 'This cigar already exists in the system');
      } else {
        toast.error('Failed to add cigar');
      }
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0C] pb-24">
      <div className="max-w-md mx-auto p-4">
        <button data-testid="back-button-add" onClick={() => navigate(-1)} className="text-white mb-4">
          ← Back
        </button>

        <h1 className="playfair text-3xl text-white font-bold mb-6">Add Cigar</h1>

        {/* AI Search Results with parsed data */}
        {aiResult?.parsed_data ? (
          <div className="space-y-4">
            {/* AI Parsed Info */}
            <div className="bg-white/5 border border-amber-500/30 rounded-xl p-6">
              <h2 className="text-xl font-bold text-amber-500 mb-4">🤖 AI Found This Cigar:</h2>
              <div className="space-y-3 text-white">
                {aiResult.parsed_data.brand && (
                  <div>
                    <span className="text-white/50">Brand:</span>
                    <span className="ml-2 font-semibold">{aiResult.parsed_data.brand}</span>
                  </div>
                )}
                {aiResult.parsed_data.name && (
                  <div>
                    <span className="text-white/50">Name:</span>
                    <span className="ml-2 font-semibold">{aiResult.parsed_data.name}</span>
                  </div>
                )}
                {aiResult.parsed_data.strength && (
                  <div>
                    <span className="text-white/50">Strength:</span>
                    <span className="ml-2">{aiResult.parsed_data.strength}</span>
                  </div>
                )}
                {aiResult.parsed_data.origin && (
                  <div>
                    <span className="text-white/50">Origin:</span>
                    <span className="ml-2">{aiResult.parsed_data.origin}</span>
                  </div>
                )}
                {aiResult.parsed_data.size && (
                  <div>
                    <span className="text-white/50">Size:</span>
                    <span className="ml-2">{aiResult.parsed_data.size}</span>
                  </div>
                )}
                {aiResult.parsed_data.wrapper && (
                  <div>
                    <span className="text-white/50">Wrapper:</span>
                    <span className="ml-2">{aiResult.parsed_data.wrapper}</span>
                  </div>
                )}
                {aiResult.parsed_data.price_range && (
                  <div>
                    <span className="text-white/50">Price Range:</span>
                    <span className="ml-2">{aiResult.parsed_data.price_range}</span>
                  </div>
                )}
                {aiResult.parsed_data.flavor_notes?.length > 0 && (
                  <div>
                    <span className="text-white/50">Flavor Notes:</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {aiResult.parsed_data.flavor_notes.map((note, idx) => (
                        <span key={idx} className="bg-amber-500/20 text-amber-300 px-3 py-1 rounded-full text-sm">
                          {note}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Existing matches in database */}
            {aiResult.searchResults && aiResult.searchResults.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-white font-bold">Similar cigars in database ({aiResult.searchResults.length}):</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {aiResult.searchResults.map((cigar) => (
                    <div
                      key={cigar.id}
                      onClick={() => navigate(`/cigar/${cigar.id}`)}
                      className="bg-white/5 border border-white/10 rounded-xl p-3 hover:border-amber-500/30 transition-all cursor-pointer"
                    >
                      <div className="flex gap-3 items-center">
                        <div className="w-12 h-12 bg-white/5 rounded-lg flex items-center justify-center overflow-hidden">
                          <img src={cigar.image_url || CIGAR_PLACEHOLDER} alt={cigar.name} className="w-full h-full object-cover rounded-lg" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-white/50">{cigar.brand}</p>
                          <h3 className="text-white font-bold text-sm">{cigar.name}</h3>
                          <div className="flex items-center gap-2">
                            <Star className="text-amber-500 fill-amber-500" size={12} />
                            <span className="text-amber-500 text-xs">{cigar.average_rating?.toFixed(1) || '0.0'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleCancelAI}
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5"
              >
                Back
              </Button>
              <Button
                onClick={async () => {
                  // Directly add the AI parsed data as a new cigar
                  setConfirming(true);
                  try {
                    const res = await axios.post(
                      `${API}/cigars/confirm-add`,
                      aiResult.parsed_data,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.success('Cigar added successfully!');
                    navigate(`/cigar/${res.data.id}`);
                  } catch (error) {
                    if (error.response?.status === 400) {
                      toast.error(error.response.data.detail || 'This cigar already exists');
                    } else {
                      toast.error('Failed to add cigar');
                    }
                  } finally {
                    setConfirming(false);
                  }
                }}
                disabled={confirming}
                className="flex-1 bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-500 hover:to-amber-700"
              >
                {confirming ? 'Adding...' : 'Add This Cigar'}
              </Button>
            </div>
          </div>
        ) : aiResult?.alreadyExists ? (
          <div className="space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
              <p className="text-green-400 text-sm">
                ✓ This cigar is already in the database! Click below to view it.
              </p>
            </div>
            <h2 className="text-xl font-bold text-white">
              {aiResult.parsed_data.brand} {aiResult.parsed_data.name}
            </h2>
            <div
              onClick={() => navigate(`/cigar/${aiResult.alreadyExists.id}`)}
              className="bg-white/5 border border-green-500/30 rounded-xl p-4 hover:border-green-500/50 transition-all cursor-pointer"
            >
              <div className="flex gap-3">
                <div className="w-16 h-16 bg-white/5 rounded-lg flex items-center justify-center overflow-hidden">
                  {aiResult.alreadyExists.image_url ? (
                    <img src={aiResult.alreadyExists.image_url} alt={aiResult.alreadyExists.name} className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <img src={CIGAR_PLACEHOLDER} alt="Cigar" className="w-full h-full object-cover rounded-lg" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-xs text-white/50">{aiResult.alreadyExists.brand}</p>
                  <h3 className="text-white font-bold">{aiResult.alreadyExists.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Star className="text-amber-500 fill-amber-500" size={14} />
                    <span className="text-amber-500 text-sm">{aiResult.alreadyExists.average_rating?.toFixed(1) || '0.0'}</span>
                    <span className="text-white/50 text-sm">{aiResult.alreadyExists.price_range}</span>
                  </div>
                  <p className="text-green-400 text-xs mt-1">✓ Click to view</p>
                </div>
              </div>
            </div>
            <Button
              onClick={() => {
                setAiResult(null);
                setSearch('');
              }}
              variant="outline"
              className="w-full border-white/10 text-white"
            >
              Search for a Different Cigar
            </Button>
          </div>
        ) : aiResult?.searchResults && aiResult.searchResults.length > 0 ? (
          /* Custom Search Results (no parsed_data) */
          <div className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <p className="text-amber-400 text-sm">
                Found {aiResult.searchResults.length} matching cigars. Click one to view it.
              </p>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {aiResult.searchResults.map((cigar) => (
                <div
                  key={cigar.id}
                  onClick={() => navigate(`/cigar/${cigar.id}`)}
                  className="bg-white/5 border border-white/10 rounded-xl p-3 hover:border-amber-500/30 transition-all cursor-pointer"
                >
                  <div className="flex gap-3 items-center">
                    <div className="w-12 h-12 bg-white/5 rounded-lg flex items-center justify-center overflow-hidden">
                      {cigar.image_url ? (
                        <img src={cigar.image_url} alt={cigar.name} className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <img src={CIGAR_PLACEHOLDER} alt="Cigar" className="w-full h-full object-cover rounded-lg" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-amber-500">{cigar.brand}</p>
                      <h3 className="text-white font-medium">{cigar.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Star className="text-amber-500 fill-amber-500" size={12} />
                        <span className="text-amber-500 text-xs">{cigar.average_rating?.toFixed(1) || '0.0'}</span>
                        {cigar.strength && <span className="text-white/40 text-xs">· {cigar.strength}</span>}
                        {cigar.price_range && <span className="text-white/40 text-xs">· {cigar.price_range}</span>}
                      </div>
                    </div>
                    <ChevronRight className="text-white/30" size={20} />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-white/50 text-sm text-center">Not what you're looking for?</p>
            <Button
              onClick={() => {
                setAiResult({ noResults: true, formData });
              }}
              variant="outline"
              className="w-full border-amber-500/30 text-amber-500"
            >
              Add as New Cigar
            </Button>
            <Button
              onClick={() => {
                setAiResult(null);
                setShowCustom(true);
              }}
              variant="outline"
              className="w-full border-white/10 text-white"
            >
              Search Again
            </Button>
          </div>
        ) : aiResult?.notFound ? (
          /* Cigar not recognized - offer custom add */
          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <p className="text-red-400 text-sm">
                "{aiResult.searchTerm}" does not appear to be a recognized cigar.
              </p>
              {aiResult.reason && (
                <p className="text-white/50 text-xs mt-2">{aiResult.reason}</p>
              )}
            </div>
            <p className="text-white/70 text-sm text-center">
              If you're sure this cigar exists, you can add it manually using Custom Search.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setAiResult(null);
                  setSearch('');
                }}
                variant="outline"
                className="flex-1 border-white/10 text-white"
              >
                Try Again
              </Button>
              <Button
                onClick={() => {
                  setAiResult(null);
                  setShowCustom(true);
                }}
                className="flex-1 bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-500 hover:to-amber-700"
              >
                Custom Search
              </Button>
            </div>
          </div>
        ) : aiResult?.canAdd ? (
          <div className="space-y-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">AI Found This Cigar:</h2>
              <div className="space-y-3 text-white">
                <div>
                  <span className="text-white/50">Brand:</span>
                  <span className="ml-2 font-semibold">{aiResult.parsed_data.brand}</span>
                </div>
                <div>
                  <span className="text-white/50">Name:</span>
                  <span className="ml-2 font-semibold">{aiResult.parsed_data.name}</span>
                </div>
                <div>
                  <span className="text-white/50">Strength:</span>
                  <span className="ml-2">{aiResult.parsed_data.strength}</span>
                </div>
                <div>
                  <span className="text-white/50">Origin:</span>
                  <span className="ml-2">{aiResult.parsed_data.origin}</span>
                </div>
                <div>
                  <span className="text-white/50">Size:</span>
                  <span className="ml-2">{aiResult.parsed_data.size}</span>
                </div>
                <div>
                  <span className="text-white/50">Wrapper:</span>
                  <span className="ml-2">{aiResult.parsed_data.wrapper}</span>
                </div>
                <div>
                  <span className="text-white/50">Price Range:</span>
                  <span className="ml-2">{aiResult.parsed_data.price_range}</span>
                </div>
                <div>
                  <span className="text-white/50">Flavor Notes:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {aiResult.parsed_data.flavor_notes?.map((note, idx) => (
                      <span key={idx} className="bg-white/10 px-3 py-1 rounded-full text-sm">
                        {note}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setAiResult(null);
                  setSearch('');
                }}
                variant="outline"
                className="flex-1 border-white/10 text-white"
              >
                Search Again
              </Button>
              <Button
                onClick={async () => {
                  setConfirming(true);
                  try {
                    const res = await axios.post(
                      `${API}/cigars/confirm-add`,
                      aiResult.parsed_data,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.success('Cigar added successfully!');
                    navigate(`/cigar/${res.data.id}`);
                  } catch (error) {
                    if (error.response?.status === 400) {
                      toast.error(error.response.data.detail || 'This cigar already exists');
                    } else {
                      toast.error('Failed to add cigar');
                    }
                  } finally {
                    setConfirming(false);
                  }
                }}
                disabled={confirming}
                className="flex-1 bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-500 hover:to-amber-700"
              >
                {confirming ? 'Adding...' : 'Add This Cigar'}
              </Button>
            </div>

            {/* Similar Cigars - name-based variants */}
            {aiResult.suggestions && aiResult.suggestions.length > 0 && (
              <div className="space-y-3">
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <p className="text-white/70 text-sm font-medium">
                    Similar Cigars
                  </p>
                </div>
                <div className="space-y-2">
                  {aiResult.suggestions.map((cigar, index) => (
                    <div
                      key={index}
                      className="bg-white/5 border border-white/10 rounded-xl p-3"
                    >
                      <div className="flex-1">
                        <p className="text-xs text-amber-500">{cigar.brand}</p>
                        <h3 className="text-white font-bold text-sm">{cigar.name}</h3>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : !showCustom ? (
          <div className="space-y-4">
            <Input
              data-testid="search-cigar-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAISearch()}
              placeholder="Search for a cigar..."
              className="bg-white/5 border-white/10 text-white"
            />
            <Button 
              data-testid="search-cigar-button" 
              onClick={handleAISearch}
              disabled={searching}
              className="w-full bg-amber-600"
            >
              {searching ? 'Searching with AI...' : 'Search'}
            </Button>
            <Button
              data-testid="ai-scanner-button"
              onClick={() => setShowScanner(true)}
              className="w-full bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-500 hover:to-amber-700"
            >
              <Camera size={18} className="mr-2" />
              AI Cigar Scanner
            </Button>
            <Button
              data-testid="custom-search-button"
              onClick={() => setShowCustom(true)}
              variant="outline"
              className="w-full border-white/10 text-white hover:bg-white/5"
            >
              Custom Search
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <Input
              data-testid="custom-brand-input"
              placeholder="Brand (e.g., Padron)"
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
            <Input
              data-testid="custom-name-input"
              placeholder="Name (optional)"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
            <Input
              data-testid="custom-strength-input"
              placeholder="Strength (optional)"
              value={formData.strength}
              onChange={(e) => setFormData({ ...formData, strength: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
            <Input
              data-testid="custom-origin-input"
              placeholder="Origin (optional)"
              value={formData.origin}
              onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
            <Input
              data-testid="custom-size-input"
              placeholder="Size (optional)"
              value={formData.size}
              onChange={(e) => setFormData({ ...formData, size: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
            <Input
              data-testid="custom-wrapper-input"
              placeholder="Wrapper (optional)"
              value={formData.wrapper}
              onChange={(e) => setFormData({ ...formData, wrapper: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
            <Input
              data-testid="custom-price-input"
              placeholder="Price Range (e.g., $10-15)"
              value={formData.price_range}
              onChange={(e) => setFormData({ ...formData, price_range: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
            <Button 
              data-testid="custom-search-submit-button"
              onClick={handleCustomSearch} 
              className="w-full bg-amber-600"
            >
              Search Cigars
            </Button>
            <Button
              onClick={() => {
                setShowCustom(false);
                setFormData({
                  brand: '',
                  name: '',
                  strength: '',
                  origin: '',
                  size: '',
                  wrapper: '',
                  price_range: ''
                });
              }}
              variant="outline"
              className="w-full border-white/10 text-white"
            >
              Back
            </Button>
          </div>
        )}
      </div>
      
      {/* AI Scanner Modal */}
      {showScanner && <CameraModal onClose={() => setShowScanner(false)} />}
      
      <BottomNav />
    </div>
  );
};

export default AddCigarPage;
